<template>
  <main>
    <section class="modal-bounds vld-parent">
      <header class="modal-title">
        Image Manager
      </header>
      <Loading
        class="vasion-loading-indicator"
        :active.sync="isLoading"
        :is-full-page="false"
        :color="loaderColor"
        loader="dots"
        :background-color="loaderBackgroundColor"
      />
      <div class="thumbnails">
        <div
          v-for="(document, index) in documents"
          :id="`thumbnail-${index}`"
          :key="document.DocumentId"
          class="thumbnail-container drop-clear"
          draggable
          @click="clickDocument(document)"
          @dragstart="dragRowStart(index)"
          @dragover="dragRowOver($event, index)"
          @drop="dragRowDrop($event, index)"
          @dragend="dragRowEnd()"
        >
          <div :class="document.selected ? 'border-green' : 'border-grey'">
            <div class="thumbnail-border">
              <ThumbnailImage
                id="thumbnail-image"
                ref="mainImage"
                class="center-thumbnail shadow"
                :documentID="document.DocumentId"
                :pageNumber="1"
                :width="120"
                :index="index"
                :keepAspectRatio="true"
                :imageString="document.imageString"
              />
            </div>
            <div class="thumbnail-footer">
              <div id="thumbnail-docname">
                [{{ document.DisplayName }}]
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <footer>
      <span class="selected-count">{{ selectedCountText }}</span>
      <VasionButton
        id="image-manager-cancel"
        classProp="secondary"
        :isRaised="false"
        @vasionButtonClicked="cancel()"
      >
        Cancel
      </VasionButton>
      <VasionButton
        id="image-manager-add-to-document"
        classProp="primary"
        :isDisabled="disableSaveButton"
        :isRaised="false"
        @vasionButtonClicked="save()"
      >
        Add To Document
      </VasionButton>
    </footer>
    <VasionGeneralModal
      id="image-manager-cancel"
      :hideButtons="true"
      :modalType="'slot'"
      buttonGroup="single"
      :sync="showLeaveDialog"
      @noButtonClick="showLeaveDialog = false"
    >
      <VasionConfirmationDialog
        message="Are you sure you want to leave? Changes have not been saved."
        @noButtonClick="showLeaveDialog = false"
        @yesButtonClick="$emit('noButtonClick')"
      />
    </VasionGeneralModal>
  </main>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import ThumbnailImage from '@/components/document/ThumbnailImage.vue';

export default {
  name: 'AttachDocumentsImageManager',
  components: {
    Loading,
    ThumbnailImage,
  },
  props: {
    documentIds: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data: function () {
    return {
      dragRowStartIndex: -1,
      dragRowTargetIndex: -1,
      isDirty: false,
      isLoading: false,
      lastContainer: null,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      documents: [],
      showLeaveDialog: false,
      showThumbnailMenu: false,
    }
  },
  computed: {
    disableSaveButton() { return this.selectedCount === 0 },
    selectedCount() { return this.documents.filter(d => d.selected === true).length },
    selectedCountText() { return this.selectedCount + " FILE" + (this.selectedCount == 1 ? "": "S") + " SELECTED" }
  },  
  async created() {
    this.isLoading = true

    for (const documentId of this.documentIds) {
      let document = await this.$store.dispatch('document/getDocumentDetailsWithPayload', { documentID: documentId })
      document.selected = false
      this.documents.push(document)
    }

    this.setup()
  },
  methods: {
    cancel() {
      if (this.isDirty) {
        this.showLeaveDialog = true
      } else {
        this.$emit('noButtonClick')
      }
    },
    clickDocument(document) {
      this.isDirty = true
      document.selected = !document.selected
    },
    clickedOutsideLeave() {
      this.routeTo = null
    },
    dragRowDrop(event)
    {
      this.lastContainer.classList.remove('drop-right')
      this.lastContainer.classList.remove('drop-left')
      event.preventDefault();
      const startIndex = this.dragRowStartIndex
      let index = this.dragRowTargetIndex
      if (startIndex == index) {
        return
      }
      this.isDirty = true
      const movedUp = index < startIndex
      
      // Now move the buttons
      if (!movedUp) {
        //if we moved the button down, then once we remove it from the button list it offsets our index
        index -= 1
      }
      var element = this.documents[startIndex];
      this.documents.splice(startIndex, 1);
      this.documents.splice(index, 0, element);
    },
    dragRowEnd()
    {
      this.dragRowTargetIndex = -1
      this.dragRowStartIndex = -1
      this.lastContainer.classList.remove('drop-right')
      this.lastContainer.classList.remove('drop-left')
    },
    dragRowOver(event, index)
    {
      if (this.dragRowStartIndex == index) {
        return
      }
      let container = document.getElementById(`thumbnail-${index}`)
      if (this.lastContainer !== null) {
        this.lastContainer.classList.remove('drop-right')
        this.lastContainer.classList.remove('drop-left')
      }
      if (event.offsetX <= 94) {
        container.classList.add('drop-left')
        this.dragRowTargetIndex = index
      }
      else {
        container.classList.add('drop-right')
        this.dragRowTargetIndex = index + 1
      }
      this.lastContainer = container
      event.preventDefault();
    },
    dragRowStart(index)
    {
      this.dragRowStartIndex = index;
    },
    async save() {
      if (this.disableSaveButton) {
        return
      }
      const param = this.documents.filter(d => d.selected === true).map(d => d.DocumentId)
      this.$emit('yesButtonClick', param) 
    },
    async setup() {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';

  main {
    width: 928px;
    max-height: 670px;
    min-height: 412px;
  }

  .center-thumbnail {
    margin: auto 0;
  }

  .modal-bounds {
    width: 928px;
    background-color: $white;
    padding: 30px 0px 0px 16px; 
  }

  .modal-title {
    @include SubtitleActive;
    font-size: 1.5em;
    margin-bottom: 10px;
  }

  footer {
    position: relative;
    bottom: 0px;
    display: flex;
    justify-content: flex-end;
    padding-right: 26px;
    padding-bottom: 20px;
  }

  .selected-count {
     margin-top: 14px;
     margin-right: 20px;
  }

  .thumbnails {
    display: flex;
    flex-flow: row wrap;
    overflow-x: auto;
    max-height: 540px;
    min-height: 268px;
  }

  .thumbnail-border {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    padding: 0px 28px 0px 28px;
    background-color: #F8F9F9;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    height: 192px;
  }

  .thumbnail-container {
    padding: 16px;
    flex: 0 0 21%;
  }

  .thumbnail-footer {
    height: 40px;
    max-height: 40px;
    overflow: hidden;
  }

  #thumbnail-docname {
    margin-left: 12px;
  }

  .shadow {
    box-shadow: 1px 0px 5px 1px $grey-100;
  }

  .border-green {
    border: 1px solid green;
    border-radius: 4px;
  }

  .border-grey {
    border: 1px solid $grey-100;
    border-radius: 4px;
  }

  .drop-clear {
    border-left: 1px solid white;
    border-right: 1px solid white;
  }

  .drop-left {
    border-left: 1px solid black !important;
  }

  .drop-right {
    border-right: 1px solid black !important;
  }
</style>
